/**
 * Adaptadores para Supabase que implementam as mesmas interfaces dos serviços de backend
 * Permite usar Supabase como drop-in replacement para o backend
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { ClientesSupabaseService } from './clientesService';
import { PropostasSupabaseService } from './propostasService';
import { SuperadminSupabaseService, TenantSupabaseService as TenantSupabaseServiceNew } from './superadminService';
import { ContratoControleQualidadeSupabaseService } from './contratoControleQualidadeService';

// Tipos das tabelas do Supabase
type Tables = Database['public']['Tables'];
type Cliente = Tables['clientes']['Row'];
type ClienteInsert = Tables['clientes']['Insert'];
type ClienteUpdate = Tables['clientes']['Update'];

type Contrato = Tables['contratos']['Row'];
type ContratoInsert = Tables['contratos']['Insert'];
type ContratoUpdate = Tables['contratos']['Update'];

type Consultor = Tables['consultores']['Row'];
type ConsultorInsert = Tables['consultores']['Insert'];
type ConsultorUpdate = Tables['consultores']['Update'];

type Tenant = Tables['tenants']['Row'];
type TenantInsert = Tables['tenants']['Insert'];
type TenantUpdate = Tables['tenants']['Update'];


// Interface base para serviços
interface BaseService<T, TInsert, TUpdate> {
  findAll(): Promise<T[]>;
  findById(id: string): Promise<T | null>;
  create(data: TInsert): Promise<T>;
  update(id: string, data: TUpdate): Promise<T>;
  delete(id: string): Promise<void>;
}

// Adaptador para Clientes
export class ClienteSupabaseService implements BaseService<Cliente, ClienteInsert, ClienteUpdate> {
  async findAll(): Promise<Cliente[]> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async findById(id: string): Promise<Cliente | null> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('id', Number(id))
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Registro não encontrado
      throw error;
    }
    return data;
  }

  async create(data: ClienteInsert): Promise<Cliente> {
    const { data: result, error } = await supabase
      .from('clientes')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async update(id: string, data: ClienteUpdate): Promise<Cliente> {
    const { data: result, error } = await supabase
      .from('clientes')
      .update(data)
      .eq('id', Number(id))
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('clientes')
      .delete()
      .eq('id', Number(id));

    if (error) throw error;
  }

  // Métodos específicos para clientes
  async findByCpf(cpf: string): Promise<Cliente | null> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('cpf', cpf)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async findByEmail(email: string): Promise<Cliente | null> {
    const { data, error } = await supabase
      .from('clientes')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }
}

// Adaptador para Contratos
export class ContratoSupabaseService implements BaseService<Contrato, ContratoInsert, ContratoUpdate> {
  async findAll(
    filterByUserId?: string,
    tenantId?: string | number | number[]
  ): Promise<Contrato[]> {
    const { applyTenantIdFilter } = await import('@/utils/tenantScope');

    let query = supabase
      .from('contratos')
      .select(`
        id,
        contrato_numero,
        cota,
        consultor_id,
        status,
        status_pagamento,
        valor_parcela,
        total_pago,
        parcelas_pagas,
        assinado,
        cliente_id,
        tenant_id,
        created_at,
        created_by,
        clientes(id, nome, cpf, email),
        grupos(id, numero_grupo, especie),
        bens(id, codigo_bem, preco_bem, tipo_bem),
        consultor:consultores!contratos_consultor_id_fkey(id, nome),
        created_by_user:profiles!contratos_created_by_fkey(id, first_name, last_name, email)
      `)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    // Filtrar por usuário (para consultores)
    if (filterByUserId) {
      // Obter IDs do consultor (numéricos) caso o usuário também esteja atrelado à tabela consultores
      const { data: consultores } = await supabase
        .from('consultores')
        .select('id')
        .eq('user_id', filterByUserId);

      if (consultores && consultores.length > 0) {
        const consultorIds = consultores.map((c: any) => c.id).join(',');
        query = query.or(`created_by.eq.${filterByUserId},consultor_id.in.(${consultorIds})`);
      } else {
        query = query.eq('created_by', filterByUserId);
      }
    }

    query = applyTenantIdFilter(query, tenantId);

    const { data, error } = await query;

    if (error) throw error;

    // Mapear os dados para garantir que consultor.nome venha da relação ou do criador
    const contratosComConsultor = (data || []).map(contrato => {
      const rawContrato = contrato as any;

      // Lógica de fallback: Prioridade 1: Relação consultor | Prioridade 2: Coluna consultor_nome | Prioridade 3: Criador
      let nomeVendedor = rawContrato.consultor?.nome;
      
      if (!nomeVendedor && rawContrato.created_by_user) {
        nomeVendedor = `${rawContrato.created_by_user.first_name || ''} ${rawContrato.created_by_user.last_name || ''}`.trim();
      }

      return {
        ...rawContrato,
        consultor: {
          id: rawContrato.consultor_id,
          nome: nomeVendedor || 'Consultor não identificado'
        }
      } as any;
    });

    return contratosComConsultor;
  }

  async findById(id: string): Promise<Contrato | null> {
    const { data, error } = await supabase
      .from('contratos')
      .select(`
        id,
        contrato_numero,
        cota,
        consultor_id,
        status,
        status_pagamento,
        valor_parcela,
        total_pago,
        data_limite,
        data_assembleia,
        valor_entrada,
        parcelas_pagas,
        assinado,
        cliente_id,
        grupo_id,
        bem_id,
        tenant_id,
        observacoes,
        metodo_pagamento,
        created_at,
        created_by,
        clientes(id, nome, cpf, email, tel1, tel2, tel_responsavel, email_responsavel),
        grupos(id, numero_grupo, especie),
        bens(id, codigo_bem, preco_bem, tipo_bem),
        consultor:consultores!contratos_consultor_id_fkey(id, nome),
        created_by_user:profiles!contratos_created_by_fkey(id, first_name, last_name, email)
      `)
      .eq('id', Number(id))
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    if (!data) return null;

    // Mapear os dados para adicionar informações do consultor
    // Usamos (data as any) para garantir acesso livre às propriedades
    const rawData = data as any;

    // Lógica de fallback para o nome do vendedor
    let nomeVendedor = rawData.consultor?.nome;
    if (!nomeVendedor && rawData.created_by_user) {
      nomeVendedor = `${rawData.created_by_user.first_name || ''} ${rawData.created_by_user.last_name || ''}`.trim();
    }

    return {
      ...rawData,
      // Garantir compatibilidade com ambos os formatos: objeto consultor e campos planos
      consultor: {
        id: rawData.consultor_id,
        nome: nomeVendedor || 'Consultor não identificado'
      },
      consultor_id: rawData.consultor_id,
      consultor_nome: nomeVendedor || 'N/A'
    } as any;
  }

  async create(data: ContratoInsert): Promise<Contrato> {
    const { data: result, error } = await supabase
      .from('contratos')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async update(id: string, data: ContratoUpdate): Promise<Contrato> {
    const { data: result, error } = await supabase
      .from('contratos')
      .update(data)
      .eq('id', Number(id))
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();

    const { error } = await supabase
      .from('contratos')
      .update({ 
        deleted_at: new Date().toISOString(),
        deleted_by: user?.id 
      } as any)
      .eq('id', Number(id));

    if (error) throw error;
  }

  // Métodos específicos para contratos
  async findByClienteId(clienteId: string): Promise<Contrato[]> {
    const { data, error } = await supabase
      .from('contratos')
      .select('*')
      .eq('cliente_id', Number(clienteId))
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }

  async findByStatus(status: string): Promise<Contrato[]> {
    const { data, error } = await supabase
      .from('contratos')
      .select('*')
      .eq('status', status)
      .is('deleted_at', null)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  }
}

// Adaptador para Consultores
export class ConsultorSupabaseService implements BaseService<Consultor, ConsultorInsert, ConsultorUpdate> {
  async findAll(tenantId?: string | number): Promise<Consultor[]> {
    let query = supabase
      .from('consultores')
      .select('*, profiles!consultores_user_id_fkey(email)')
      .is('deleted_at', null)
      .order('nome', { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', Number(tenantId));
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(c => ({
      ...c,
      email: c.email || (c as any).profiles?.email || ""
    })) as Consultor[];
  }

  // Método listarConsultores para compatibilidade com o unifiedService
  async listarConsultores(tenantId?: string | number): Promise<Consultor[]> {
    // Log removido para evitar spam no console
    return this.findAll(tenantId);
  }

  async findById(id: string): Promise<Consultor | null> {
    const { data, error } = await supabase
      .from('consultores')
      .select('*, profiles!consultores_user_id_fkey(email)')
      .eq('id', Number(id))
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }

    return {
      ...data,
      email: data.email || (data as any).profiles?.email || ""
    } as Consultor;
  }

  async create(data: ConsultorInsert): Promise<Consultor> {
    const { data: result, error } = await supabase
      .from('consultores')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async update(id: string, data: ConsultorUpdate): Promise<Consultor> {
    const { data: result, error } = await supabase
      .from('consultores')
      .update(data)
      .eq('id', Number(id))
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('consultores')
      .delete()
      .eq('id', Number(id));

    if (error) throw error;
  }

  // Métodos adicionais para compatibilidade com o unifiedService
  async buscarConsultor(id: string): Promise<Consultor | null> {
    return this.findById(id);
  }

  async criarConsultor(data: ConsultorInsert): Promise<Consultor> {
    return this.create(data);
  }

  async atualizarConsultor(id: string, data: ConsultorUpdate): Promise<Consultor> {
    return this.update(id, data);
  }

  async excluirConsultor(id: string): Promise<void> {
    return this.delete(id);
  }

  async listarEquipe(supervisorId: string, tenantId?: string | number): Promise<{ lideres: Consultor[], relacionadores: Consultor[] }> {
    let query = supabase
      .from('consultores')
      .select('*, profiles!consultores_user_id_fkey(email)')
      .eq('supervisor_id', Number(supervisorId))
      .eq('status', 'ativo')
      .is('deleted_at', null)
      .order('nome', { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', Number(tenantId));
    }

    const { data, error } = await query;

    if (error) throw error;

    const consultores = (data || []).map(c => ({
      ...c,
      email: c.email || (c as any).profiles?.email || ""
    })) as Consultor[];

    // Separar líderes e relacionadores
    const lideres = consultores.filter(c => c.tipo === 'lider');
    const relacionadores = consultores.filter(c => c.tipo === 'relacionador');

    return { lideres, relacionadores };
  }

  async listarRelacionadores(liderId: string, tenantId?: string | number): Promise<Consultor[]> {
    let query = supabase
      .from('consultores')
      .select('*, profiles!consultores_user_id_fkey(email)')
      .eq('supervisor_id', Number(liderId))
      .eq('tipo', 'relacionador')
      .eq('status', 'ativo')
      .is('deleted_at', null)
      .order('nome', { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', Number(tenantId));
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(c => ({
      ...c,
      email: c.email || (c as any).profiles?.email || ""
    })) as Consultor[];
  }

  async listarSupervisores(tenantId?: string | number): Promise<Consultor[]> {
    let query = supabase
      .from('consultores')
      .select('*, profiles!consultores_user_id_fkey(email)')
      .eq('tipo', 'supervisor')
      .eq('status', 'ativo')
      .is('deleted_at', null)
      .order('nome', { ascending: true });

    if (tenantId) {
      query = query.eq('tenant_id', Number(tenantId));
    }

    const { data, error } = await query;

    if (error) throw error;

    return (data || []).map(c => ({
      ...c,
      email: c.email || (c as any).profiles?.email || ""
    })) as Consultor[];
  }

  async ativarConsultor(id: string): Promise<Consultor> {
    return this.update(id, { status: 'ativo' } as ConsultorUpdate);
  }

  async inativarConsultor(id: string): Promise<Consultor> {
    return this.update(id, { status: 'inativo' } as ConsultorUpdate);
  }
}

// Adaptador para Tenants
export class TenantSupabaseService implements BaseService<Tenant, TenantInsert, TenantUpdate> {
  async findAll(): Promise<Tenant[]> {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('status', 'ATIVO')
      .order('nome', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async findById(id: string): Promise<Tenant | null> {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('id', Number(id))
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    return data;
  }

  async create(data: TenantInsert): Promise<Tenant> {
    const { data: result, error } = await supabase
      .from('tenants')
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async update(id: string, data: TenantUpdate): Promise<Tenant> {
    const { data: result, error } = await supabase
      .from('tenants')
      .update(data)
      .eq('id', Number(id))
      .select()
      .single();

    if (error) throw error;
    return result;
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('tenants')
      .delete()
      .eq('id', Number(id));

    if (error) throw error;
  }
}


// Wrapper para ClientesSupabaseService que adiciona métodos necessários
class ClientesSupabaseServiceWrapper extends ClienteSupabaseService {
  private clientesService = new ClientesSupabaseService();

  async listarClientes(page = 1, per_page = 10, search?: string, tenantId?: string, filterByUserId?: string) {
    return await this.clientesService.listarClientes(page, per_page, search, tenantId, filterByUserId);
  }

  async buscarCliente(id: string) {
    return await this.clientesService.buscarCliente(id);
  }

  async criarCliente(data: any) {
    return await this.clientesService.criarCliente(data);
  }

  async atualizarCliente(id: string, data: any) {
    return await this.clientesService.atualizarCliente(id, data);
  }

  async excluirCliente(id: string) {
    return await this.clientesService.excluirCliente(id);
  }

  async excluirClientesEmLote(ids: string[]) {
    return await this.clientesService.excluirClientesEmLote(ids);
  }

  async verificarCPFExistente(cpf: string, ignoreClienteId?: string) {
    return await this.clientesService.verificarCPFExistente(cpf, ignoreClienteId);
  }

  async uploadDocumento(clienteId: string, file: File, tipo: string, tenantId?: string, userId?: string) {
    return await this.clientesService.uploadDocumento(clienteId, file, tipo, tenantId, userId);
  }

  async excluirDocumento(clienteId: string, documentoId: string) {
    return await this.clientesService.excluirDocumento(clienteId, documentoId);
  }

  async listarDocumentos(clienteId: string) {
    return await this.clientesService.listarDocumentos(clienteId);
  }

  async getDocumentUrl(path: string) {
    return await this.clientesService.getDocumentUrl(path);
  }
}

// Factory para criar os serviços apropriados
export const createSupabaseServices = () => {
  const consultoresService = new ConsultorSupabaseService();

  return {
    clientes: new ClientesSupabaseServiceWrapper(),
    contratos: new ContratoSupabaseService(),
    consultores: consultoresService,
    tenants: new TenantSupabaseServiceNew(),
    propostas: new PropostasSupabaseService(),
    superadmin: new SuperadminSupabaseService(),
    contratoControleQualidade: new ContratoControleQualidadeSupabaseService(),
  };
};

// Instância singleton dos serviços Supabase
export const supabaseServices = createSupabaseServices();
